// useSSESubscription：通用 SSE 事件订阅 hook。
//
// 与 useConnectionStatus 共享 /events 通道概念，但本 hook 侧重单一事件名的
// 数据回调（用于 query invalidation 或本地状态合并），不负责 daemon 健康状态。
//
// 设计要点：
//   - 同一浏览器 tab 内同一路径只保留一条 EventSource，多个 hook 共享并按事件名分发。
//   - 仅订阅指定 eventType；其他事件直接忽略。回调拿到已 JSON.parse 的 payload。
//   - 网络断开 / 401 → 浏览器原生 EventSource 自动重连，无需额外逻辑。
//
// 测试约定：组件层 vitest 通常 mock 掉本 hook 或断网（避免 jsdom 没有
// EventSource）。
//
// 示例：
//   useSSESubscription("instance.update", (payload) => {
//     if (payload.instance_id === id) queryClient.invalidateQueries({ queryKey: ["instance", id] });
//   });

import { useEffect, useRef } from "react";
import { retainSSEChannel } from "./sseChannel";

export interface SSEMessage {
  // daemon eventHub 发出的 payload；外层调用者可断言为具体类型。
  [key: string]: unknown;
}

export type SSEHandler = (payload: SSEMessage) => void;

export interface UseSSESubscriptionOptions {
  // path 默认 /events，测试可覆写。
  path?: string;
  // enabled=false 时不订阅（如 server-side render 或显式 pause）。
  enabled?: boolean;
}

export function useSSESubscription(
  eventType: string,
  handler: SSEHandler,
  options: UseSSESubscriptionOptions = {},
): void {
  // 保持 handler 最新引用，避免闭包内调用到旧的（不重订阅 SSE）。
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (options.enabled === false) return;

    const path = options.path ?? "/events";
    const channel = retainSSEChannel(path);
    if (!channel) return;

    const listener = (e: MessageEvent) => {
      let payload: SSEMessage;
      try {
        payload = JSON.parse(e.data) as SSEMessage;
      } catch {
        // 非 JSON：忽略，daemon 当前所有事件都是 JSON。
        return;
      }
      handlerRef.current(payload);
    };

    channel.addEventListener(eventType, listener as EventListener);
    return () => {
      channel.removeEventListener(eventType, listener as EventListener);
      channel.release();
    };
  }, [eventType, options.enabled, options.path]);
}
